// src/components/QueueList.js
//
// Visão em lista da fila do dia: a ordem vertical É a ordem de disparo.
// O primeiro card é o próximo a ser postado.
//
// Alternativa à visão de mapa (QueueFlow), que continua existindo.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Empty, Tooltip, message } from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CheckCircleFilled,
  DeleteOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  HolderOutlined,
  ThunderboltFilled,
} from "@ant-design/icons";

// Paleta clara, inspirada no Material/Google.
const C = {
  page: "#f8f9fa",
  card: "#ffffff",
  border: "#e3e6ea",
  borderStrong: "#dadce0",
  text: "#202124",
  muted: "#5f6368",
  faint: "#80868b",
  blue: "#1a73e8",
  blueSoft: "#e8f0fe",
  green: "#1e8e3e",
  greenSoft: "#e6f4ea",
  orange: "#e37400",
  orangeSoft: "#fef7e0",
  red: "#d93025",
  purple: "#8430ce",
  purpleSoft: "#f3e8fd",
};

// Status em que o item já saiu (ou está saindo) e não deve ser reordenado.
const LOCKED_STATUS = ["SENT", "CLAIMED"];

function isLocked(item) {
  return LOCKED_STATUS.includes(item?.status);
}

function isSent(item) {
  return item?.status === "SENT";
}

function formatBRL(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  if (Number.isNaN(n)) return null;
  return n.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function formatHour(readyAt) {
  const seconds = readyAt?._seconds ?? readyAt?.seconds;
  if (!seconds) return null;

  return new Date(seconds * 1000).toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

const STATUS_STYLE = {
  READY: { label: "na fila", fg: C.blue, bg: C.blueSoft },
  CLAIMED: { label: "enviando", fg: C.orange, bg: C.orangeSoft },
  SENT: { label: "postado", fg: C.green, bg: C.greenSoft },
  PAUSED: { label: "pausado", fg: C.muted, bg: "#f1f3f4" },
  FAILED: { label: "falhou", fg: C.red, bg: "#fce8e6" },
};

function Chip({ children, fg = C.muted, bg = "#f1f3f4", title }) {
  return (
    <span
      title={title}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        height: 22,
        padding: "0 9px",
        borderRadius: 999,
        background: bg,
        color: fg,
        fontSize: 12,
        fontWeight: 500,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

export default function QueueList({
  items,
  onEdit,
  onDelete,
  onSaveOrder, // async (orderedIds) => Promise
}) {
  // Lista COMPLETA (inclusive postados). O reorder precisa dela inteira:
  // mandar só os visíveis faria os ocultos colidirem no sortIndex.
  const [list, setList] = useState(items || []);
  const [showSent, setShowSent] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [saving, setSaving] = useState(false);

  const savingRef = useRef(false);

  useEffect(() => {
    const ordered = [...(items || [])].sort(
      (a, b) => (a.sortIndex ?? 999999) - (b.sortIndex ?? 999999)
    );
    setList(ordered);
  }, [items]);

  const sentCount = useMemo(
    () => list.filter(isSent).length,
    [list]
  );

  const visible = useMemo(
    () => (showSent ? list : list.filter((item) => !isSent(item))),
    [list, showSent]
  );

  // Primeira posição que ainda pode receber um item: furar fila não pode
  // jogar um produto para antes do que já saiu.
  const firstOpenIndex = useMemo(() => {
    const idx = list.findIndex((item) => !isLocked(item));
    return idx === -1 ? list.length : idx;
  }, [list]);

  const persist = async (nextList) => {
    const previous = list;

    setList(nextList);

    if (savingRef.current) return;

    savingRef.current = true;
    setSaving(true);

    try {
      await onSaveOrder(nextList.map((item) => item.id));
    } catch (e) {
      // volta ao estado anterior para a tela não mentir sobre a ordem salva
      setList(previous);
      message.error(e.message || "Erro ao salvar a ordem");
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  // Trabalha por id, não por índice: a lista exibida pode estar filtrada.
  const moveTo = (id, targetIndex) => {
    const from = list.findIndex((i) => i.id === id);
    if (from === -1) return;
    if (targetIndex < 0 || targetIndex >= list.length) return;
    if (from === targetIndex) return;

    const next = [...list];
    const [moved] = next.splice(from, 1);
    next.splice(targetIndex, 0, moved);

    persist(next);
  };

  const moveBy = (id, delta) => {
    const from = list.findIndex((i) => i.id === id);
    if (from === -1) return;
    moveTo(id, from + delta);
  };

  const dropOn = (targetId) => {
    if (!dragId || dragId === targetId) return;
    const to = list.findIndex((i) => i.id === targetId);
    if (to === -1) return;
    moveTo(dragId, to);
  };

  // "Fura fila": manda para a frente de todo mundo que ainda não saiu.
  const cutLine = (id) => {
    const from = list.findIndex((i) => i.id === id);
    if (from === firstOpenIndex) {
      message.info("Esse já é o próximo a ser postado.");
      return;
    }
    moveTo(id, firstOpenIndex);
  };

  const pendingCount = list.length - sentCount;

  return (
    <div style={{ maxWidth: 820, margin: "0 auto" }}>
      {/* barra de contexto */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 16,
          flexWrap: "wrap",
        }}
      >
        <div style={{ fontSize: 13, color: C.muted }}>
          {pendingCount > 0 ? (
            <>
              <b style={{ color: C.text }}>{pendingCount}</b>{" "}
              {pendingCount === 1 ? "produto na fila" : "produtos na fila"}
              {saving && (
                <span style={{ color: C.faint }}> · salvando…</span>
              )}
            </>
          ) : (
            "Nada pendente na fila deste dia."
          )}
        </div>

        {sentCount > 0 && (
          <Button
            size="small"
            type="text"
            icon={showSent ? <EyeInvisibleOutlined /> : <EyeOutlined />}
            onClick={() => setShowSent((v) => !v)}
            style={{ color: C.blue, fontWeight: 500 }}
          >
            {showSent
              ? "Ocultar postados"
              : `Mostrar postados (${sentCount})`}
          </Button>
        )}
      </div>

      {!visible.length ? (
        <div
          style={{
            background: C.card,
            border: `1px solid ${C.border}`,
            borderRadius: 14,
            padding: 32,
          }}
        >
          <Empty
            description={
              <span style={{ color: C.muted }}>
                {sentCount > 0
                  ? "Tudo deste dia já foi postado."
                  : "Nenhum produto na fila deste dia."}
              </span>
            }
          />
        </div>
      ) : (
        visible.map((item) => {
          const locked = isLocked(item);
          const sent = isSent(item);
          const status = STATUS_STYLE[item.status] || STATUS_STYLE.READY;

          const pFrom = formatBRL(item.priceFrom);
          const pTo = formatBRL(item.priceTo);
          const hour = formatHour(item.readyAt);

          const realIndex = list.findIndex((i) => i.id === item.id);

          // Numeração conta só os pendentes: o que importa é quem sai primeiro.
          const queuePosition =
            list.slice(0, realIndex).filter((i) => !isSent(i)).length + 1;

          const dragging = dragId === item.id;
          const over = overId === item.id && dragId !== item.id;

          return (
            <div
              key={item.id}
              draggable={!locked}
              onDragStart={() => setDragId(item.id)}
              onDragEnd={() => {
                setDragId(null);
                setOverId(null);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                if (overId !== item.id) setOverId(item.id);
              }}
              onDrop={(e) => {
                e.preventDefault();
                dropOn(item.id);
                setDragId(null);
                setOverId(null);
              }}
              style={{
                display: "flex",
                gap: 14,
                alignItems: "center",
                padding: "14px 16px",
                marginBottom: 8,
                borderRadius: 14,
                background: C.card,
                border: `1px solid ${over ? C.blue : C.border}`,
                boxShadow: dragging
                  ? "0 8px 24px rgba(60,64,67,.18)"
                  : "0 1px 2px rgba(60,64,67,.06)",
                opacity: dragging ? 0.5 : sent ? 0.72 : 1,
                transition:
                  "border-color .12s ease, box-shadow .12s ease, opacity .12s ease",
              }}
            >
              {/* posição / check */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flexShrink: 0,
                }}
              >
                {!locked ? (
                  <HolderOutlined
                    style={{
                      color: C.faint,
                      cursor: "grab",
                      fontSize: 15,
                    }}
                  />
                ) : (
                  <span style={{ width: 15 }} />
                )}

                {sent ? (
                  <CheckCircleFilled
                    style={{ color: C.green, fontSize: 26 }}
                  />
                ) : (
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 999,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background:
                        queuePosition === 1 ? C.blue : C.blueSoft,
                      color: queuePosition === 1 ? "#fff" : C.blue,
                      fontWeight: 600,
                      fontSize: 13,
                    }}
                  >
                    {queuePosition}
                  </div>
                )}
              </div>

              {/* imagem */}
              {item.imageUrl && (
                <img
                  src={item.editedImageUrl || item.imageUrl}
                  alt={item.productName}
                  style={{
                    width: 52,
                    height: 52,
                    objectFit: "cover",
                    borderRadius: 10,
                    flexShrink: 0,
                    border: `1px solid ${C.border}`,
                  }}
                />
              )}

              {/* conteúdo */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontWeight: 500,
                    fontSize: 15,
                    color: C.text,
                    marginBottom: item.description ? 2 : 6,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={item.productName}
                >
                  {item.productName}
                </div>

                {item.description && (
                  <div
                    style={{
                      fontSize: 13,
                      color: C.muted,
                      whiteSpace: "pre-wrap",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      marginBottom: 8,
                      lineHeight: 1.4,
                    }}
                    title={item.description}
                  >
                    {item.description}
                  </div>
                )}

                <div
                  style={{
                    display: "flex",
                    gap: 6,
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <Chip fg={status.fg} bg={status.bg}>
                    {status.label}
                  </Chip>

                  {hour && (
                    <Chip title="Horário previsto de disparo">🕐 {hour}</Chip>
                  )}

                  {(pFrom || pTo) && (
                    <span style={{ fontSize: 13, color: C.muted }}>
                      {pFrom && (
                        <span
                          style={{
                            textDecoration: "line-through",
                            color: C.faint,
                          }}
                        >
                          {pFrom}
                        </span>
                      )}
                      {pTo && (
                        <b style={{ marginLeft: 6, color: C.text }}>{pTo}</b>
                      )}
                    </span>
                  )}

                  {item.couponName && (
                    <Chip fg={C.purple} bg={C.purpleSoft}>
                      {item.couponName}
                      {item.couponValue ? ` ${item.couponValue}%` : ""}
                    </Chip>
                  )}
                </div>
              </div>

              {/* ações */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexShrink: 0,
                }}
              >
                {!locked && (
                  <>
                    <Tooltip title="Fura fila: posta esse primeiro">
                      <Button
                        type="text"
                        shape="circle"
                        icon={
                          <ThunderboltFilled
                            style={{
                              color:
                                queuePosition === 1 ? C.faint : C.orange,
                            }}
                          />
                        }
                        disabled={queuePosition === 1 || saving}
                        onClick={() => cutLine(item.id)}
                      />
                    </Tooltip>

                    <Tooltip title="Subir">
                      <Button
                        type="text"
                        shape="circle"
                        icon={<ArrowUpOutlined />}
                        disabled={realIndex <= firstOpenIndex || saving}
                        onClick={() => moveBy(item.id, -1)}
                      />
                    </Tooltip>

                    <Tooltip title="Descer">
                      <Button
                        type="text"
                        shape="circle"
                        icon={<ArrowDownOutlined />}
                        disabled={realIndex === list.length - 1 || saving}
                        onClick={() => moveBy(item.id, 1)}
                      />
                    </Tooltip>
                  </>
                )}

                <Tooltip title="Editar">
                  <Button
                    type="text"
                    shape="circle"
                    icon={<EditOutlined />}
                    onClick={() => onEdit(item)}
                  />
                </Tooltip>

                <Tooltip title="Deletar">
                  <Button
                    type="text"
                    shape="circle"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => onDelete(item)}
                  />
                </Tooltip>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
